import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { FiX, FiLock, FiShield, FiCheckCircle } from "react-icons/fi";
import { motion, AnimatePresence } from "framer-motion";
import { BASE_URL, changePasswordApi } from "../../../api/api";

const ChangePasswordModal = () => {
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        current_password: "",
        password: "",
        confirmation_password: "",
    });

    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === "Escape") navigate(-1);
        };
        document.addEventListener("keydown", handleEscape);
        return () => document.removeEventListener("keydown", handleEscape);
    }, [navigate]);

    const handleClose = () => navigate(-1);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
        setError("");
        setSuccess("");
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        const { current_password, password, confirmation_password } = formData;

        if (!current_password || !password || !confirmation_password) {
            setError("All fields are required.");
            setLoading(false);
            return;
        }

        if (password !== confirmation_password) {
            setError("New passwords do not match.");
            setLoading(false);
            return;
        }

        try {
            const session = JSON.parse(
                sessionStorage.getItem("studentSession"),
            );
            const token = session?.token;

            if (!token) {
                setError("Authentication token not found.");
                setLoading(false);
                return;
            }

            const response = await fetch(`${BASE_URL}${changePasswordApi}`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    current_password,
                    password,
                    confirmation_password,
                }),
            });

            const data = await response.json();

            if (response.ok) {
                setSuccess("Password updated successfully.");
                setFormData({
                    current_password: "",
                    password: "",
                    confirmation_password: "",
                });
                // Optional: Close modal after success
                setTimeout(() => navigate(-1), 2000);
            } else {
                setError(data.message || "Failed to change password.");
            }
        } catch (err) {
            setError("Something went wrong. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return createPortal(
        <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4">
            {/* Backdrop */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                onClick={handleClose}
            />

            {/* Modal Container */}
            <motion.div
                initial={{ scale: 0.95, opacity: 0, y: 20 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                className="relative w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl"
            >
                {/* Header */}
                <div className="flex items-center justify-between border-b border-gray-100 p-6">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-800">
                            Change 
                            <span className="text-yellow-500"> Password</span>
                        </h2>
                        <p className="text-xs text-gray-500">
                            Update your account security
                        </p>
                    </div>
                    <button
                        className="rounded-full p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                        onClick={handleClose}
                    >
                        <FiX size={20} />
                    </button>
                </div>

                {/* Form */}
                <form className="space-y-4 p-6" onSubmit={handleSubmit}>
                    {/* Old Password */}
                    <div className="space-y-1.5">
                        <label className="text-sm font-semibold text-gray-700">
                            Current Password
                        </label>
                        <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                                <FiLock size={16} />
                            </span>
                            <input
                                type="password"
                                name="current_password"
                                placeholder="••••••••"
                                className="w-full rounded-lg border border-gray-200 bg-gray-50 py-2.5 pl-10 pr-4 text-sm outline-none transition-all focus:border-yellow-600 focus:bg-white focus:ring-2 focus:ring-yellow-100"
                                value={formData.current_password}
                                onChange={handleChange}
                            />
                        </div>
                    </div>

                    {/* New Password */}
                    <div className="space-y-1.5">
                        <label className="text-sm font-semibold text-gray-700">
                            New Password
                        </label>
                        <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                                <FiShield size={16} />
                            </span>
                            <input
                                type="password"
                                name="password"
                                placeholder="••••••••"
                                className="w-full rounded-lg border border-gray-200 bg-gray-50 py-2.5 pl-10 pr-4 text-sm outline-none transition-all focus:border-yellow-600 focus:bg-white focus:ring-2 focus:ring-yellow-100"
                                value={formData.password}
                                onChange={handleChange}
                            />
                        </div>
                    </div>

                    {/* Confirm Password */}
                    <div className="space-y-1.5">
                        <label className="text-sm font-semibold text-gray-700">
                            Confirm New Password
                        </label>
                        <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                                <FiCheckCircle size={16} />
                            </span>
                            <input
                                type="password"
                                name="confirmation_password"
                                placeholder="••••••••"
                                className="w-full rounded-lg border border-gray-200 bg-gray-50 py-2.5 pl-10 pr-4 text-sm outline-none transition-all focus:border-yellow-600 focus:bg-white focus:ring-2 focus:ring-yellow-100"
                                value={formData.confirmation_password}
                                onChange={handleChange}
                            />
                        </div>
                    </div>

                    {/* Status Messages */}
                    <AnimatePresence mode="wait">
                        {error && (
                            <motion.p
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: "auto" }}
                                exit={{ opacity: 0, height: 0 }}
                                className="rounded-md bg-red-50 p-2.5 text-xs font-medium text-red-600"
                            >
                                {error}
                            </motion.p>
                        )}
                        {success && (
                            <motion.p
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: "auto" }}
                                exit={{ opacity: 0, height: 0 }}
                                className="rounded-md bg-green-50 p-2.5 text-xs font-medium text-green-600"
                            >
                                {success}
                            </motion.p>
                        )}
                    </AnimatePresence>

                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full rounded-lg bg-yellow-500 py-3 text-sm font-bold text-black cursor-pointer shadow-lg transition-all hover:bg-yellow-600 active:scale-95 disabled:opacity-50"
                    >
                        {loading ? "Updating..." : "Update Password"}
                    </button>
                </form>

                {/* Branding Accent */}
                <div className="h-1 w-full bg-yellow-500" />
            </motion.div>
        </div>,
        document.body,
    );
};

export default ChangePasswordModal;
